/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  filter helper functions
 */

import type { UndefinedOrDate } from '@zcode-ui/controls'
import { format, isValid, parse, subDays } from 'date-fns'

interface ParseDate {
  (date?: undefined): undefined
  (date: null): null
  (date: Date | string): Date
  <T extends null | undefined | Date>(date: string | T): Date | T
  (date?: string | null | Date): Date | null | undefined
}

export const FILTER_DATE_FORMAT = 'MM-dd-yyyy' as const
export const FILTER_INPUT_DATE_FORMAT = 'MM/dd/yyyy' as const

export const formatDateForFilter = (
  date?: Date | null | string,
  formatAs: string = FILTER_DATE_FORMAT
) => {
  const normalizedDate = typeof date === 'string' ? new Date(date.replaceAll('-', '/')) : date
  if (!normalizedDate || !isValid(normalizedDate)) {
    return undefined
  }
  return format(normalizedDate, formatAs)
}

export const parseDateFromFilter = ((date?: string | null | Date) => {
  return typeof date === 'string' ? parse(date, FILTER_DATE_FORMAT, new Date()) : date
}) as ParseDate

export const getDateBeforeToday = (now: number = Date.now()) => subDays(new Date(now), 1)

export const validDateFilter = (
  selectedDate: Date,
  startDate: UndefinedOrDate,
  endDate: UndefinedOrDate
) => {
  const errors = []

  if (selectedDate.getTime() === startDate?.getTime() && !endDate) {
    errors.push({ type: 'end', message: "End date can't be the same as the start date" })
  }

  if (selectedDate.getTime() < getDateBeforeToday().getTime()) {
    errors.push({ type: 'end', message: "Start date can't be a date in the past" })
  }

  return errors
}
